import React from 'react'
import { useIntl } from 'react-intl'
import { Link } from 'react-router-dom'
import {
    Box,
    Checkbox,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material'
import { ArrowForwardRounded } from '@mui/icons-material'
import { StudentRatingState } from '../../../../features/types'
import { tableStyles, ui, tone as tones } from '../../../../utils/uiTokens'
import { LEVEL_COLORS, percentOf, studentLevel } from '../ratingLevel'

import { useHeadScope } from '../../../head/headScope'

interface Props {
    students: StudentRatingState[]
    monthNumber?: number
    weekNumber?: number
    groupId?: number
    filter?: string
    // последний открытый отчёт: строка подсвечена и в неё прокручиваем при возврате
    highlightId?: number | null
    onRowClick?: (student: StudentRatingState) => void
    checkedIds?: number[]
    onToggleCheck?: (id: number) => void
    onToggleAll?: () => void
}

// Первые три места - как на подиуме: золото, серебро, бронза
const rankColors = ['#F0A22E', '#98A2B3', '#B4713A']

const styles = {
    rank: (index: number) => ({
        width: 28,
        height: 28,
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '13px',
        fontWeight: 600,
        color: index < 3 ? '#fff' : ui.sub,
        backgroundColor: index < 3 ? rankColors[index] : ui.track,
    }),
    avatar: {
        width: 34,
        height: 34,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '12.5px',
        fontWeight: 600,
        color: ui.pink,
        backgroundColor: ui.pinkSoft,
        flexShrink: 0,
    },
    name: {
        fontSize: '14px',
        fontWeight: 500,
        color: ui.ink,
    },
    group: {
        fontSize: '12px',
        color: ui.muted,
    },
    value: {
        fontSize: '15px',
        fontWeight: 600,
        color: ui.ink,
    },
    hint: {
        fontSize: '11.5px',
        color: ui.muted,
    },
    chip: (tone: 'done' | 'partial' | 'none') => ({
        display: 'inline-block',
        padding: '4px 12px',
        borderRadius: '100px',
        fontSize: '13px',
        fontWeight: 600,
        color:
            tone === 'done' ? tones.ok.text : tone === 'partial' ? tones.warn.text : ui.muted,
        backgroundColor:
            tone === 'done' ? tones.ok.bg : tone === 'partial' ? tones.warn.bg : ui.track,
    }),
    total: {
        fontSize: '17px',
        fontWeight: 700,
        color: ui.ink,
    },
}

// Балл, процент в белом бейдже и полоса под ними - в цвете уровня ученика
export const TotalScore: React.FC<{ total: number; max: number }> = ({ total, max }) => {
    const level = studentLevel(total, max)
    const c = level ? LEVEL_COLORS[level] : null
    const pct = percentOf(total, max)

    return (
        <Box sx={{ display: 'inline-flex', flexDirection: 'column', gap: '6px' }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                <Box sx={styles.total}>
                    {total}
                    <Box component="span" sx={{ ...styles.hint, ml: 0.5 }}>/{max}</Box>
                </Box>
                {c && (
                    <Box
                        sx={{
                            px: 1,
                            py: '2px',
                            borderRadius: '100px',
                            background: ui.card,
                            color: c.text,
                            fontSize: '12.5px',
                            fontWeight: 700,
                        }}
                    >
                        {pct}%
                    </Box>
                )}
            </Box>
            {c && (
                <Box sx={{ width: 90, height: 4, borderRadius: 2, background: ui.card, overflow: 'hidden' }}>
                    <Box sx={{ width: `${Math.min(pct, 100)}%`, height: '100%', background: c.accent }} />
                </Box>
            )}
        </Box>
    )
}

export const levelRowSx = (total: number, max: number) => {
    const level = studentLevel(total, max)
    if (!level) return {}
    const c = LEVEL_COLORS[level]
    return {
        backgroundColor: c.bg,
        '&:hover': { backgroundColor: c.bg, filter: 'brightness(0.98)' },
        '& .MuiTableCell-root:first-of-type': { boxShadow: `inset 4px 0 0 ${c.accent}` },
    }
}

const RatingTable: React.FC<Props> = ({
    students = [],
    monthNumber,
    weekNumber,
    groupId,
    filter,
    highlightId,
    onRowClick,
    checkedIds = [],
    onToggleCheck,
    onToggleAll,
}) => {
    const { formatMessage } = useIntl()
    const { basePath } = useHeadScope()
    const allChecked = students.length > 0 && checkedIds.length >= students.length

    const initials = (student: StudentRatingState) =>
        `${student.firstName?.[0] || ''}${student.lastName?.[0] || ''}`.toUpperCase()

    const tone = (value: number, max: number) => {
        if (!max || !value) return 'none' as const
        return value >= max ? ('done' as const) : ('partial' as const)
    }

    const reportLink = (student: StudentRatingState) => {
        const params = new URLSearchParams()
        if (monthNumber) params.set('monthNumber', String(monthNumber))
        if (weekNumber) params.set('weekNumber', String(weekNumber))
        // группа и поиск нужны отчёту, чтобы «Алдыңғы / Келесі» шли в том же порядке, что и таблица
        if (groupId) params.set('groupId', String(groupId))
        if (filter) params.set('filter', filter)

        return `${basePath}/rating/${student.userSubjectEnrollId}?${params.toString()}`
    }

    return (
        <TableContainer
            component={Paper}
            sx={tableStyles.container}
            elevation={0}
        >
            <Table>
                <TableHead sx={tableStyles.head}>
                    <TableRow>
                        <TableCell padding="checkbox">
                            <Checkbox
                                size="small"
                                checked={allChecked}
                                indeterminate={checkedIds.length > 0 && !allChecked}
                                onChange={() => onToggleAll?.()}
                                sx={{ color: ui.pink, '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: ui.pink } }}
                            />
                        </TableCell>
                        <TableCell>#</TableCell>
                        <TableCell>{formatMessage({ id: 'student' })}</TableCell>
                        <TableCell align="center">{formatMessage({ id: 'video' })}</TableCell>
                        <TableCell align="center">{formatMessage({ id: 'test' })}</TableCell>
                        <TableCell align="center">{formatMessage({ id: 'conspect' })}</TableCell>
                        <TableCell align="center">{formatMessage({ id: 'zachet' })}</TableCell>
                        <TableCell align="center">{formatMessage({ id: 'practice' })}</TableCell>
                        <TableCell align="center">{formatMessage({ id: 'totalPoint' })}</TableCell>
                        <TableCell align="right">{formatMessage({ id: 'details' })}</TableCell>
                    </TableRow>
                </TableHead>

                <TableBody>
                    {students.length ? (
                        students.map((student, index) => (
                            <TableRow
                                key={student.userSubjectEnrollId}
                                id={`rating-row-${student.userSubjectEnrollId}`}
                                sx={{
                                    ...tableStyles.row,
                                    ...levelRowSx(student.totalPoint, student.maxPoint),
                                    cursor: onRowClick ? 'pointer' : 'default',
                                    ...(highlightId === student.userSubjectEnrollId && {
                                        outline: `2px solid ${ui.pink}`,
                                        outlineOffset: '-2px',
                                    }),
                                }}
                                onClick={() => onRowClick?.(student)}
                            >
                                <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                                    <Checkbox
                                        size="small"
                                        checked={checkedIds.includes(student.userSubjectEnrollId)}
                                        onChange={() => onToggleCheck?.(student.userSubjectEnrollId)}
                                        sx={{ color: ui.pink, '&.Mui-checked': { color: ui.pink } }}
                                    />
                                </TableCell>

                                <TableCell>
                                    <Box sx={styles.rank(index)}>{index + 1}</Box>
                                </TableCell>

                                <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        <Box sx={styles.avatar}>{initials(student)}</Box>
                                        <Box>
                                            <Box sx={styles.name}>
                                                {student.firstName} {student.lastName}
                                            </Box>
                                            <Box sx={styles.group}>{student.groupTitle}</Box>
                                        </Box>
                                    </Box>
                                </TableCell>

                                <TableCell align="center">
                                    <Box sx={styles.value}>{student.videoPoint}</Box>
                                    <Box sx={styles.hint}>
                                        {student.videoDone}/{student.videoTotal} · 85%+
                                    </Box>
                                </TableCell>

                                <TableCell align="center">
                                    <Box sx={styles.value}>{student.testPoint}</Box>
                                    <Box sx={styles.hint}>{student.testPercent}%</Box>
                                </TableCell>

                                <TableCell align="center">
                                    <Box sx={styles.chip(tone(student.conspectPoint, student.conspectMax))}>
                                        {student.conspectPoint} / {student.conspectMax}
                                    </Box>
                                </TableCell>

                                <TableCell align="center">
                                    <Box sx={styles.chip(student.zachetPoint ? 'partial' : 'none')}>
                                        {student.zachetPoint}
                                    </Box>
                                </TableCell>

                                <TableCell align="center">
                                    <Box sx={styles.chip(student.practicePoint ? 'done' : 'none')}>
                                        {student.practicePoint}
                                    </Box>
                                </TableCell>

                                <TableCell align="center">
                                    <TotalScore total={student.totalPoint} max={student.maxPoint} />
                                </TableCell>

                                <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                                    <Link to={reportLink(student)}>
                                        <IconButton sx={{ background: ui.pinkSoft, borderRadius: '10px' }}>
                                            <ArrowForwardRounded sx={{ color: ui.pink, fontSize: 18 }} />
                                        </IconButton>
                                    </Link>
                                </TableCell>
                            </TableRow>
                        ))
                    ) : (
                        <TableRow>
                            <TableCell
                                align="center"
                                colSpan={10}
                            >
                                {formatMessage({ id: 'noData' })}
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>

            </Table>
        </TableContainer>
    )
}

export default RatingTable
